import {
  Check, Contact, Copy, CreditCard, EyeOff, ListTodo, Lock, Settings, Trash2, type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { ContactOutcomeDialog } from "@/features/contact-outcome";
import { LeadTasks } from "@/features/task-list";
import { ownersKnown } from "@/lib/tasks";
import { ContactNotes } from "@/features/contact-timeline";
import { DeleteLeadDialog } from "@/features/lead-delete";
import { OpportunityAppointments } from "@/features/opportunity-appointments";
import { SamePhoneCaveat, SamePhoneRecords } from "@/features/same-phone";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { contactName, stageGroup, stageSectionOf, stageSlugs, stageText } from "@/lib/contacts";
import {
  FORM_FIELDS, HIDDEN_FIELDS_PREF, defaultScheduleTime, dropAction, localInputValue, readHiddenFields,
  scheduleTimeProblem, sourceText, toggleHidden, type FormField,
} from "@/lib/opportunities";
import {
  useContact, useFirms, useMoveLead, useSaveUiPrefs, useUiPrefs, useUpdateContact, type ContactInput,
} from "@/lib/queries";
import { STAGE_SECTIONS, sectionText } from "@/lib/stages";
import { seesCampaigns, atLeast, type LeadCard } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";
import { ContactLists, ReopenDialog } from "@/routes/contact";

/**
 * An opportunity, opened from its card on the board (ADR-078).
 *
 * GoHighLevel's Edit opportunity, drawn as it draws it: a centred dialog, its sections in a sidebar
 * on the left, a form of contact and opportunity details, and Delete, Cancel and Update at the foot.
 * What core-api saves is editable and Update sends only what changed (`PATCH /leads/{id}`, the same
 * rules as the contact form); what the platform does not have yet is drawn and locked, so the screen
 * does not change shape when it arrives. Every write refetches everything under `leads`, so the
 * board under the dialog shows the change.
 *
 * A stage picked here follows the board's rule (`dropAction`): a move between the open stages (a
 * scheduled one asks for its time), the outcome for an exit, an admin's reopen out of one. The
 * dial lists follow the stage (ADR-078).
 *
 * Appointments are real (ACL-411) — `features/opportunity-appointments.tsx`, which reuses the same
 * booking dialog and drawer the Calendars page uses. Tasks and Payments are still the locked
 * placeholders described above.
 */
export function OpportunityDialog({
  leadId, onClose, section = "details",
}: {
  leadId: string | null;
  onClose: () => void;
  /** The section it opens on: a card's notes icon opens it on Notes. */
  section?: string;
}) {
  return (
    <Dialog open={leadId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="h-[min(58rem,calc(100dvh-2rem))] max-h-[calc(100dvh-2rem)] max-w-[62.5rem] rounded-lg"
        overlayClassName="bg-gray-12/30 backdrop-blur-md"
        closeClassName="mr-2 mt-2 rounded-full bg-gray-3 p-2 text-text hover:bg-gray-4 [&_svg]:size-5"
        // GoHighLevel opens on the form, not with a ring on the close button.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {leadId && <OpportunityBody key={`${leadId}:${section}`} leadId={leadId} onClose={onClose} section={section} />}
      </DialogContent>
    </Dialog>
  );
}

/** GoHighLevel's sections, in its order and with its words. */
const SECTIONS: { value: string; label: string }[] = [
  { value: "details", label: "Opportunity details" },
  { value: "appointments", label: "Book or update appointment" },
  { value: "tasks", label: "Tasks" },
  { value: "notes", label: "Notes" },
  { value: "payments", label: "Payments" },
  { value: "associated", label: "Associated objects" },
];

function OpportunityBody({ leadId, onClose, section }: { leadId: string; onClose: () => void; section: string }) {
  const { data: contact, isPending, error, refetch } = useContact(leadId);
  const [tab, setTab] = React.useState(() => (SECTIONS.some((x) => x.value === section) ? section : "details"));
  const sees = seesCampaigns(useAuth().user?.role);

  if (isPending) {
    return (
      <>
        <DialogTitle className="sr-only">Opportunity</DialogTitle>
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </>
    );
  }
  if (error || !contact) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <>
        <DialogTitle className="sr-only">Opportunity</DialogTitle>
        <div className="flex flex-1 items-center justify-center p-6">
          {missing ? (
            <EmptyState
              title="Contact not found"
              description={
                sees
                  ? "It may have been deleted, or belong to a campaign you do not work."
                  : "It may have been deleted, or belong to leads you do not work."
              }
              action={<Button variant="outline" onClick={onClose}>Back to the board</Button>}
            />
          ) : (
            <ErrorState
              error={error}
              title="Could not load the contact"
              action={<Button variant="outline" onClick={() => void refetch()}>Try again</Button>}
            />
          )}
        </div>
      </>
    );
  }
  // Keyed on the record, so a save that refetches it starts the form from what was saved.
  return <OpportunityForm key={`${contact.id}:${contact.updated_at ?? ""}`} contact={contact} tab={tab} onTab={setTab} onClose={onClose} />;
}

interface Draft {
  name: string;
  email: string;
  phone: string;
  firm: string;
  stage: string;
}

const NO_FIRM = "none";

/** GoHighLevel's field: 36px, 14px text, and white even when this platform cannot save it yet. */
const FIELD = "h-10 text-sm disabled:bg-surface disabled:text-text";

function draftOf(contact: LeadCard): Draft {
  return {
    name: contactName(contact) ?? "",
    email: contact.email ?? "",
    phone: contact.phone,
    firm: contact.firm ? String(contact.firm.id) : NO_FIRM,
    stage: contact.stage ?? "",
  };
}

/** GoHighLevel keeps one name; the platform keeps a first and a last. The first word is the first. */
function splitName(name: string): { first: string | null; last: string | null } {
  const [first, ...rest] = name.trim().split(/\s+/);
  return { first: first || null, last: rest.join(" ") || null };
}

function OpportunityForm({
  contact, tab, onTab, onClose,
}: {
  contact: LeadCard;
  tab: string;
  onTab: (tab: string) => void;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const admin = atLeast(user?.role, "admin");
  const supervisor = atLeast(user?.role, "supervisor");
  const toast = useToast();
  const update = useUpdateContact();
  const stage = useStageRequest();
  const firms = useFirms();
  const [draft, setDraft] = React.useState(() => draftOf(contact));
  const [hideEmpty, setHideEmpty] = React.useState(false);
  const hidden = useHiddenFields();
  const [problem, setProblem] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const name = contactName(contact) ?? "No name";
  const source = sourceText(contact.source);
  const set = (key: keyof Draft) => (value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setProblem(null);
  };

  const save = () => {
    if (!draft.name.trim()) {
      onTab("details");
      setProblem("A contact name is required.");
      return;
    }
    const input: ContactInput & { id: string } = { id: contact.id };
    const { first, last } = splitName(draft.name);
    if (draft.name.trim() !== (contactName(contact) ?? "")) {
      input.first_name = first;
      input.last_name = last;
    }
    // The e-mail is a rep's too: Send contract needs it (ACL-541).
    if (draft.email.trim() !== (contact.email ?? "")) input.email = draft.email.trim() || null;
    if (supervisor) {
      if (draft.phone.trim() !== contact.phone) input.phone = draft.phone.trim();
      if (draft.firm !== NO_FIRM && Number(draft.firm) !== (contact.firm?.id ?? null)) input.firm_id = Number(draft.firm);
    }
    const moved = draft.stage && draft.stage !== contact.stage ? draft.stage : null;
    // A stage is its own route and may ask a question first (a time, an outcome), so it goes after.
    const moveStage = () => {
      if (moved) stage.request(contact, moved);
      else onClose();
    };
    if (Object.keys(input).length === 1) {
      moveStage();
      return;
    }
    update.mutate(input, {
      onSuccess: () => {
        toast.success("Opportunity saved");
        moveStage();
      },
      onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not save. Please try again."),
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/contacts/${contact.id}`);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy", "Open the record and copy the address bar instead.");
    }
  };

  const locked = supervisor ? undefined : "A supervisor changes this";
  const soon = "Not on the platform yet";
  // A field shows unless the person hid it (Manage fields) or it is empty while empty ones are hidden.
  const on = (field: FormField) => !hidden.fields.includes(field);
  const shown = (field: FormField, value: string | null) => on(field) && !(hideEmpty && !(value ?? "").trim());

  return (
    <>
      <div className="shrink-0 px-6 pb-4 pt-6 pr-16">
        <DialogTitle className="text-lg font-medium text-text">Edit "{name}"</DialogTitle>
        <DialogDescription className="mt-3 text-sm text-text-muted">
          Add and edit opportunity details, tasks, notes and appointments.
        </DialogDescription>
      </div>
      <Tabs
        value={tab}
        onValueChange={onTab}
        orientation="vertical"
        className="mx-6 flex min-h-0 flex-1 flex-col border-t border-border md:flex-row"
      >
        <div className="flex shrink-0 flex-col md:w-56 md:border-r md:border-border md:pr-3">
          <TabsList className="gap-0.5 overflow-x-auto border-b-0 py-3 scrollbar-subtle md:flex-col md:items-stretch">
            {SECTIONS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className={cn(
                  "mb-0 whitespace-nowrap rounded-md border-b-0 px-2 py-2 text-left text-xs font-normal",
                  "hover:bg-surface-hover data-[state=active]:bg-accent-3 data-[state=active]:font-medium",
                  "data-[state=active]:text-accent-11",
                )}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <ManageFormFields hidden={hidden.fields} onChange={hidden.set} saving={hidden.saving} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-subtle md:pl-5">
          <TabsContent value="details" className="pb-4">
            <form
              id="opportunity-form"
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
            >
              <div className="flex items-center justify-between border-b border-border py-4">
                <h3 className="flex items-center gap-2 text-base font-medium text-text">
                  Contact details
                  <Contact className="size-4 text-text-muted" aria-hidden />
                </h3>
                <label className="flex items-center gap-2 text-sm text-text">
                  <input
                    type="checkbox"
                    className="accent-accent-9"
                    checked={hideEmpty}
                    onChange={(e) => setHideEmpty(e.target.checked)}
                  />
                  Hide empty fields
                </label>
              </div>
              {hidden.fields.length > 0 && (
                <div className="mt-4 flex items-center justify-between gap-3 rounded-md bg-accent-2 px-3 py-2 text-xs text-accent-11">
                  <span className="flex items-center gap-2">
                    <EyeOff className="size-3.5" aria-hidden />
                    {hidden.fields.length === 1 ? "1 field is" : `${hidden.fields.length} fields are`} hidden by Manage fields.
                  </span>
                  <button type="button" className="font-medium underline-offset-2 hover:underline" onClick={() => hidden.set([])}>
                    Show all
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 gap-x-3 gap-y-5 pt-5 sm:grid-cols-2">
                <GhlField label="Primary contact name" htmlFor="opp-name" required>
                  <Input id="opp-name" className={FIELD} value={draft.name} onChange={(e) => set("name")(e.target.value)} autoComplete="off" />
                </GhlField>
                {shown("email", draft.email) && (
                  <GhlField label="Primary email" htmlFor="opp-email">
                    <Input id="opp-email" className={FIELD} type="email" value={draft.email} onChange={(e) => set("email")(e.target.value)} autoComplete="off" />
                  </GhlField>
                )}
                {shown("phone", draft.phone) && (
                  <GhlField label="Primary phone" htmlFor="opp-phone" hint={locked}>
                    <Input id="opp-phone" className={FIELD} value={draft.phone} disabled={!supervisor} onChange={(e) => set("phone")(e.target.value)} autoComplete="off" />
                  </GhlField>
                )}
                {shown("additional_contacts", null) && (
                  <GhlField label="Additional contacts (Max: 10)" htmlFor="opp-additional" hint={soon}>
                    <Select disabled>
                      <SelectTrigger id="opp-additional" className={FIELD}><SelectValue placeholder="Add additional contacts" /></SelectTrigger>
                      <SelectContent />
                    </Select>
                  </GhlField>
                )}
              </div>

              <h3 className="mt-6 border-b border-border pb-3 text-base font-medium text-text">Opportunity details</h3>
              <div className="grid grid-cols-1 gap-x-3 gap-y-5 pt-5 sm:grid-cols-2">
                <GhlField label="Opportunity name" htmlFor="opp-title" required className="sm:col-span-2" hint="The contact's name">
                  <Input id="opp-title" className={FIELD} value={draft.name} disabled />
                </GhlField>
                <GhlField label="Pipeline" htmlFor="opp-pipeline" hint={locked}>
                  <Select value={draft.firm} onValueChange={set("firm")} disabled={!supervisor}>
                    <SelectTrigger id="opp-pipeline" className={FIELD}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {!contact.firm && <SelectItem value={NO_FIRM}>No firm</SelectItem>}
                      {(firms.data ?? []).map((f) => (
                        <SelectItem key={f.id} value={String(f.id)}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </GhlField>
                <GhlField label="Stage" htmlFor="opp-stage">
                  <StageSelect id="opp-stage" value={draft.stage} onChange={set("stage")} />
                </GhlField>
                {on("status") && (
                  <GhlField label="Status" htmlFor="opp-status" hint="Follows the stage">
                    <Select value={stageGroup(contact.stage) === "exit" ? "closed" : "open"} disabled>
                      <SelectTrigger id="opp-status" className={FIELD}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </GhlField>
                )}
                {shown("value", null) && (
                  <GhlField label="Value" htmlFor="opp-value" hint={soon}>
                    <Input id="opp-value" className={FIELD} value="$ 0" disabled />
                  </GhlField>
                )}
                {shown("owner", null) && (
                  <GhlField label="Owner" htmlFor="opp-owner" hint={soon}>
                    <Select disabled>
                      <SelectTrigger id="opp-owner" className={FIELD}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent />
                    </Select>
                  </GhlField>
                )}
                {shown("followers", null) && (
                  <GhlField label="Followers" htmlFor="opp-followers" hint={soon}>
                    <Select disabled>
                      <SelectTrigger id="opp-followers" className={FIELD}><SelectValue placeholder="Add followers" /></SelectTrigger>
                      <SelectContent />
                    </Select>
                  </GhlField>
                )}
                {shown("business_name", null) && (
                  <GhlField label="Business name" htmlFor="opp-business" hint={soon}>
                    <Input id="opp-business" className={FIELD} placeholder="Enter business name" disabled />
                  </GhlField>
                )}
                {on("source") && (
                  <GhlField label="Source" htmlFor="opp-source">
                    <Input id="opp-source" className={FIELD} value={source.detail ? `${source.label} · ${source.detail}` : source.label} disabled />
                  </GhlField>
                )}
              </div>
              {problem && <p className="mt-4 text-sm text-danger-11" role="alert">{problem}</p>}
            </form>
          </TabsContent>
          <TabsContent value="appointments" className="py-4">
            <OpportunityAppointments contact={contact} />
          </TabsContent>
          <TabsContent value="tasks" className="py-4">
            {/* Real once core-api has tasks (ACL-408); "coming soon" before, as it was. */}
            {ownersKnown(contact) ? (
              <LeadTasks leadId={contact.id} />
            ) : (
              <ComingSoon icon={ListTodo} title="Tasks" what="Follow-ups assigned to a person, with a due date." />
            )}
          </TabsContent>
          <TabsContent value="notes" className="py-4">
            <ContactNotes contact={contact} />
          </TabsContent>
          <TabsContent value="payments" className="py-4">
            <ComingSoon icon={CreditCard} title="Payments" what="Invoices and payments for this contact." />
          </TabsContent>
          <TabsContent value="associated">
            <Associated contact={contact} />
          </TabsContent>
        </div>
      </Tabs>

      <div className="mx-6 flex shrink-0 flex-col gap-3 border-t border-border py-5 sm:flex-row sm:items-center sm:justify-between">
        <dl className="space-y-1 text-xs text-text">
          <div className="flex gap-1">
            <dt>Created by:</dt>
            <dd className="text-accent-11">{source.label}</dd>
          </div>
          <div className="flex gap-1">
            <dt>Created on:</dt>
            <dd>{formatDateTime(contact.created_at)}</dd>
          </div>
          <div className="flex items-center gap-1">
            <dt>Audit log:</dt>
            <dd className="flex items-center gap-1">
              <Link to={`/contacts/${contact.id}`} className="text-accent-11 hover:underline">{contact.id.slice(0, 20)}</Link>
              <button type="button" onClick={() => void copyLink()} aria-label="Copy link" className="text-accent-11">
                <Copy className="size-3" />
              </button>
            </dd>
          </div>
        </dl>
        <div className="flex items-center gap-2">
          {admin && (
            <Button type="button" size="lg" className="h-11 border-danger-6 px-7 text-danger-9 hover:bg-danger-2" aria-label="Delete contact" onClick={() => setDeleting(true)}>
              <Trash2 />
            </Button>
          )}
          <Button type="button" size="lg" className="h-11 px-12 font-medium" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            form="opportunity-form"
            variant="default"
            size="lg"
            className="h-11 px-12 font-medium"
            loading={update.isPending}
            onClick={tab === "details" ? undefined : save}
          >
            Update
          </Button>
        </div>
      </div>

      <DeleteLeadDialog open={deleting} onOpenChange={setDeleting} contact={contact} onDeleted={onClose} />
      {stage.dialogs}
    </>
  );
}

/** A labelled field the way GoHighLevel draws one: a larger label over the control. */
/**
 * The fields a person hid from the form, kept in their own `ui_prefs` so the form looks the same on
 * any browser they sign in on. A change shows at once and is saved behind it.
 */
function useHiddenFields(): { fields: FormField[]; set: (fields: FormField[]) => void; saving: boolean } {
  const prefs = useUiPrefs();
  const save = useSaveUiPrefs();
  const toast = useToast();
  const [fields, setFields] = React.useState<FormField[] | null>(null);
  const set = (next: FormField[]) => {
    setFields(next);
    save.mutate(
      { [HIDDEN_FIELDS_PREF]: next.length ? next : null },
      { onError: () => toast.error("Could not save your fields", "They are back to how they were.") },
    );
  };
  React.useEffect(() => {
    if (save.isError) setFields(null);
  }, [save.isError]);
  return { fields: fields ?? readHiddenFields(prefs.data), set, saving: save.isPending };
}

const FIELD_SECTIONS = [
  { id: "contact", title: "Contact details" },
  { id: "opportunity", title: "Opportunity details" },
] as const;

/**
 * GoHighLevel's "Manage fields": a switch per field for whether the form shows it. A required field
 * is always shown, and says so with a lock rather than a switch that does nothing.
 */
function ManageFormFields({
  hidden, onChange, saving,
}: {
  hidden: readonly FormField[];
  onChange: (fields: FormField[]) => void;
  saving: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const total = FORM_FIELDS.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group mb-2 mt-auto hidden items-center gap-2 self-stretch rounded-md px-2.5 py-2 text-sm font-medium",
            "text-accent-11 transition-[background-color,transform] duration-150 hover:bg-accent-3 active:scale-[0.98]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40 data-[state=open]:bg-accent-3 md:flex",
          )}
        >
          <Settings
            className="size-4 transition-transform duration-300 group-hover:rotate-45 group-data-[state=open]:rotate-90"
            aria-hidden
          />
          Manage fields
          {hidden.length > 0 && (
            <Badge tone="accent" size="sm" className="ml-auto bg-accent-4">{hidden.length} hidden</Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        avoidCollisions={false}
        // Over the sections' column, not the form: the fields stay readable while they are switched.
        className="flex max-h-[min(34rem,var(--radix-popover-content-available-height))] w-[21rem] flex-col p-0"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-text">Manage fields</p>
            <p className="mt-0.5 text-xs text-text-muted">Pick what this form shows.</p>
          </div>
          <span className="shrink-0 rounded-full bg-gray-3 px-2 py-0.5 text-xs font-medium tabular-nums text-text-muted">
            {total - hidden.length}/{total}
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2 scrollbar-subtle">
          {FIELD_SECTIONS.map(({ id, title }) => (
            <section key={id} className="py-1">
              <p className="px-2 pb-1 pt-1 text-2xs font-semibold uppercase tracking-wide text-text-subtle">{title}</p>
              {FORM_FIELDS.filter((f) => f.section === id).map((f) => {
                const on = !hidden.includes(f.id);
                const switchId = `manage-field-${f.id}`;
                return f.required ? (
                  <div key={f.id} className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm text-text-muted">
                    <span>{f.label}</span>
                    <span className="flex items-center gap-1 text-xs text-text-subtle">
                      <Lock className="size-3" aria-hidden />
                      Required
                    </span>
                  </div>
                ) : (
                  <label
                    key={f.id}
                    htmlFor={switchId}
                    className={cn(
                      "flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-surface-hover",
                      on ? "text-text" : "text-text-muted",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {f.label}
                      {!on && <EyeOff className="size-3.5 text-text-subtle" aria-label="Hidden" />}
                    </span>
                    <Switch id={switchId} checked={on} onCheckedChange={() => onChange(toggleHidden(hidden, f.id))} />
                  </label>
                );
              })}
            </section>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2.5">
          <span className="flex items-center gap-1.5 px-1 text-xs text-text-muted" aria-live="polite">
            {saving ? (
              <>
                <Spinner className="size-3" /> Saving…
              </>
            ) : (
              <>
                <Check className="size-3.5 text-success-11" aria-hidden /> Saved
              </>
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="sm" disabled={hidden.length === 0} onClick={() => onChange([])}>
              Show all
            </Button>
            <Button type="button" size="sm" onClick={() => setOpen(false)}>Done</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GhlField({
  label, htmlFor, required, hint, className, children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("space-y-2", className)} title={hint}>
      <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-sm font-medium text-text">
        {label}
        {required && <span className="text-danger-9" aria-hidden>*</span>}
      </label>
      {children}
    </div>
  );
}

/** The stage as a control: every stage of the catalog by group. */
function StageSelect({ id, value, onChange }: { id: string; value: string; onChange: (stage: string) => void }) {
  const slugs = stageSlugs();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className={FIELD}><SelectValue placeholder="No stage" /></SelectTrigger>
      <SelectContent>
        {STAGE_SECTIONS.map((group) => {
          const inGroup = slugs.filter((s) => stageSectionOf(s) === group);
          if (inGroup.length === 0) return null;
          return (
            <SelectGroup key={group}>
              <SelectLabel>{sectionText(group).label}</SelectLabel>
              {inGroup.map((s) => (
                <SelectItem key={s} value={s} description={group === "exit" || group === "recovery" ? "By an outcome" : undefined}>
                  {stageText(s).label}
                </SelectItem>
              ))}
            </SelectGroup>
          );
        })}
      </SelectContent>
    </Select>
  );
}

/** The records this one is tied to: the dial lists it is on and the contacts with its phone. */
function Associated({ contact }: { contact: LeadCard }) {
  const siblings = contact.siblings ?? [];
  return (
    <div className="space-y-6 py-2">
      <section>
        <h3 className="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-text-subtle">Dial lists</h3>
        <ContactLists contact={contact} />
      </section>
      <section className="px-4">
        <h3 className="pb-2 text-xs font-medium uppercase tracking-wide text-text-subtle">Same phone</h3>
        {siblings.length === 0 ? (
          <p className="text-sm text-text-muted">No other contact has this phone.</p>
        ) : (
          <>
            <SamePhoneCaveat className="pb-2" />
            <div className="rounded-lg border border-border px-3">
              <SamePhoneRecords siblings={siblings} phone={contact.phone} caveat={false} />
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/** A module the platform does not have yet, in the place it will take. */
function ComingSoon({ icon, title, what }: { icon: LucideIcon; title: string; what: string }) {
  return (
    <EmptyState
      icon={icon}
      title={title}
      description={
        <>
          This feature is coming soon.
          <span className="mt-1 block text-text-subtle">{what}</span>
        </>
      }
    />
  );
}

// --- one rule for a drag and a pick ---------------------------------------------------------------

type Who = { id: string; stage: string | null; dnc: boolean; first_name: string | null; last_name: string | null };
type Pending =
  | { kind: "outcome" | "reopen"; leadId: string; stage: string }
  | { kind: "schedule"; card: Who; stage: string };

/**
 * A stage asked for — by a drop on the board or a pick in the dialog — done the board's way
 * (`dropAction`). A move is made at once (optimistically, `useMoveLead`) and core-api moves the
 * dial lists with it; a scheduled stage first asks when the lead is to be called, since the
 * dialer waits for that time; an exit asks for the outcome; leaving one is an admin's reopen; a
 * refusal says why. The outcome and reopen dialogs need the full record, which the board's card
 * is not, so they load it first. Render `dialogs` once.
 */
export function useStageRequest() {
  const { user } = useAuth();
  const toast = useToast();
  const move = useMoveLead();
  const [pending, setPending] = React.useState<Pending | null>(null);

  const request = React.useCallback(
    (card: Who, to: string) => {
      const action = dropAction(card, to, user?.role);
      if (!action) return;
      if (action.kind === "refused") {
        toast.info("Not moved", action.reason);
        return;
      }
      if (action.kind === "move") {
        move.mutate(
          { leadId: card.id, stage: action.stage },
          {
            onError: (e) =>
              toast.error(
                `Could not move ${contactName(card) ?? "the contact"}`,
                e instanceof ApiError ? e.message : "Please try again.",
              ),
          },
        );
        return;
      }
      if (action.kind === "schedule") {
        setPending({ kind: "schedule", card, stage: action.stage });
        return;
      }
      setPending({ kind: action.kind, leadId: card.id, stage: action.stage });
    },
    [move, toast, user?.role],
  );

  /** A scheduled stage's time again, for a lead already in it: the same booking, moved. */
  const reschedule = React.useCallback(
    (card: Who) => card.stage && setPending({ kind: "schedule", card, stage: card.stage }),
    [],
  );

  const done = React.useCallback(() => setPending(null), []);
  const dialogs = !pending ? null : pending.kind === "schedule" ? (
    <ScheduleDialog card={pending.card} stage={pending.stage} onDone={done} />
  ) : (
    <PendingDialog pending={pending} onDone={done} />
  );
  return { request, reschedule, dialogs };
}

/** When a lead moved into a scheduled stage is to be called. The dial lists wait until then. */
function ScheduleDialog({ card, stage, onDone }: { card: Who; stage: string; onDone: () => void }) {
  const toast = useToast();
  const move = useMoveLead();
  const [value, setValue] = React.useState(() => defaultScheduleTime(new Date()));
  const [problem, setProblem] = React.useState<string | null>(null);
  const name = contactName(card) ?? "the contact";
  const label = stageText(stage).label;
  const rebooking = card.stage === stage;
  const submit = () => {
    const why = scheduleTimeProblem(value, new Date());
    if (why) {
      setProblem(why);
      return;
    }
    const until = new Date(value).toISOString();
    move.mutate(
      { leadId: card.id, stage, until },
      {
        onSuccess: () => {
          toast.success(`${label} for ${formatDateTime(until)}`, `${name} is not called before then.`);
          onDone();
        },
        onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not save. Please try again."),
      },
    );
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{rebooking ? `Change the time for ${name}` : `${label}: when?`}</DialogTitle>
          <DialogDescription>
            {name} moves to {label}. Every dial list waits for this time, then the dialer calls them first.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <DialogBody className="space-y-4">
            <Field label="Call them at" htmlFor="schedule-at" hint="Your time." error={problem ?? undefined} required>
              <Input
                id="schedule-at"
                type="datetime-local"
                value={value}
                min={localInputValue(new Date())}
                invalid={Boolean(problem)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValue(e.target.value);
                  setProblem(null);
                }}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
            <Button type="submit" loading={move.isPending}>{rebooking ? "Change time" : `Move to ${label}`}</Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function PendingDialog({
  pending, onDone,
}: {
  pending: Extract<Pending, { kind: "outcome" | "reopen" }>;
  onDone: () => void;
}) {
  const toast = useToast();
  const { data: contact, error } = useContact(pending.leadId);
  React.useEffect(() => {
    if (!error) return;
    toast.error("Could not open the contact", error instanceof ApiError ? error.message : "Please try again.");
    onDone();
  }, [error, onDone, toast]);
  if (!contact) return null;
  const onOpenChange = (open: boolean) => !open && onDone();
  return pending.kind === "outcome" ? (
    <ContactOutcomeDialog open onOpenChange={onOpenChange} contact={contact} forStage={pending.stage} />
  ) : (
    <ReopenDialog open onOpenChange={onOpenChange} contact={contact} into={pending.stage} />
  );
}
